/**
 * SuperAdminPanel — ATOMS Design v2 shell
 * Role: atoms_admin only
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AtomsLogo      from '../../assets/AtomsLogo.png';
import OverviewTab    from './OverviewTab';
import SuperOrgsTab   from './SuperOrgsTab';
import SuperMachinesTab from './SuperMachinesTab';
import SuperUsersTab  from './SuperUsersTab';

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const BackIcon     = 'M19 12H5 M12 19l-7-7 7-7';
const DashIcon     = 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10';
const OrgIcon      = 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';
const CpuIcon      = 'M12 2a2 2 0 012 2v1h4a1 1 0 011 1v3h1a1 1 0 011 1v6a1 1 0 01-1 1h-1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3H3a1 1 0 01-1-1V9a1 1 0 011-1h1V5a1 1 0 011-1h4V4a2 2 0 012-2z';
const UsersIcon    = 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75';
const ShieldIcon   = 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z';

const NAV_ICONS = { overview: DashIcon, orgs: OrgIcon, machines: CpuIcon, users: UsersIcon };

function NavItem({ value, label, active, onClick }) {
  return (
    <button
      onClick={() => onClick(value)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', height: 36, padding: '0 10px',
        borderRadius: 'var(--r-md)', border: 'none',
        background: active ? 'var(--ai-soft)' : 'transparent',
        color: active ? 'var(--ai)' : 'var(--ink-500)',
        fontFamily: 'var(--font-sans)', fontSize: 13,
        fontWeight: active ? 500 : 400,
        cursor: 'pointer', transition: 'all var(--t-fast)',
        marginBottom: 2, textAlign: 'left',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-raised)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon d={NAV_ICONS[value] || DashIcon} size={15} color={active ? 'var(--ai)' : 'var(--ink-500)'} />
      {label}
    </button>
  );
}

export default function SuperAdminPanel() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const role      = localStorage.getItem('role') || 'user';

  const [activeTab,  setActiveTab]  = useState(location.state?.tab || 'overview');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (role !== 'atoms_admin') { navigate('/app/dashboard', { replace: true }); return; }
    if (location.state?.refresh) {
      if (location.state?.tab) setActiveTab(location.state.tab);
      setRefreshKey(k => k + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  if (role !== 'atoms_admin') return null;

  const navItems = [
    { value: 'overview', label: 'Overview'       },
    { value: 'orgs',     label: 'Organisations'  },
    { value: 'machines', label: 'Machines'        },
    { value: 'users',    label: 'Users'           },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', overflow: 'hidden', fontFamily: 'var(--font-sans)' }}>

      {/* ── Header ── */}
      <header style={{
        height: 56, flexShrink: 0,
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-hair)',
        display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 14,
      }}>
        <button
          onClick={() => navigate('/app/dashboard')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-md)', padding: '5px 10px',
            color: 'var(--ink-500)', fontSize: 12,
            fontFamily: 'var(--font-sans)', cursor: 'pointer',
            transition: 'all var(--t-fast)',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <Icon d={BackIcon} size={13} />
          Back
        </button>

        <img src={AtomsLogo} alt="ATOMS" style={{ height: 24, width: 24, objectFit: 'contain' }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-900)', lineHeight: 1.2 }}>
            ATOMS Super Admin
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 1 }}>
            Full system access — all organisations, machines &amp; users
          </div>
        </div>

        {/* ATOMS Admin badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--ai-soft)', color: 'var(--ai)',
          border: '1px solid rgba(105,56,239,0.2)',
          borderRadius: 'var(--r-sm)', padding: '4px 10px',
          fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600,
        }}>
          <Icon d={ShieldIcon} size={13} color="var(--ai)" />
          ATOMS Admin
        </div>
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside style={{
          width: 220, flexShrink: 0,
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border-hair)',
          padding: '20px 8px',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-300)', padding: '0 10px', marginBottom: 8 }}>
            System
          </div>

          {navItems.map(item => (
            <NavItem key={item.value} active={activeTab === item.value} onClick={setActiveTab} {...item} />
          ))}

          <div style={{ flex: 1 }} />

          <button
            onClick={() => navigate('/app/dashboard')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-300)',
              transition: 'all var(--t-fast)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-raised)'; e.currentTarget.style.color = 'var(--ink-500)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--ink-300)'; }}
          >
            <Icon d={BackIcon} size={13} />
            Back to App
          </button>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 36px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {activeTab === 'overview'  && <OverviewTab      key={`ov-${refreshKey}`} />}
              {activeTab === 'orgs'      && <SuperOrgsTab     key={`og-${refreshKey}`} />}
              {activeTab === 'machines'  && <SuperMachinesTab key={`sm-${refreshKey}`} />}
              {activeTab === 'users'     && <SuperUsersTab    key={`su-${refreshKey}`} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
